import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Card, Collapse, List, Button, Dropdown, Menu } from 'antd';

import Permissions from 'components/Permissions';
import HeaderForm from 'components/Form/HeaderForm';
import { getActivitiesInfo } from 'helpers/activityHelpers';
import withAuthentication from 'hoc/authentication';
import UserrequestContact from 'components/UserrequestContact/UserrequestContact';
import { getLayerUrl } from 'helpers/userrequestHelpers';

import { updateDocuments, uploadDocuments } from 'modules/userrequest';
import { getActivityValue } from 'modules/activities-management';

import ParkingsList from './ParkingsList';
import Activity from './Activity';

import styles from './Summary.module.scss';

/**
 * Return base64 image of canvas from selector
 * @param {string} selector DOM selector
 * @returns {array} Array of base64
 */
export const getCanvasPictures = selector =>
  [...document.querySelectorAll(selector)]
    .map(canvas => canvas.toDataURL());

const { Panel } = Collapse;

class Summary extends React.Component {
  handleExportClick = async () => {
    const { layer } = this.props;
    const downloadLink = await getLayerUrl(layer);
    if (downloadLink) {
      global.location = downloadLink;
    }
  }

  handleDownloadableClick = async ({ key: downloadKey }) => {
    const { data: { downloadables }, documents = [] } = this.props;

    const hasActivityPictures = documents.some(({ key }) => key.substr(0, 8) === 'activity');

    if (!hasActivityPictures) {
      await this.uploadDocuments();
    }

    global.location = downloadables[downloadKey].url;
  }

  getActivityProps = activity => {
    const {
      activitySchema,
      data: { properties: { activityTypes = [] } },
    } = this.props;

    const activityForm = activityTypes.length ? activityTypes : activitySchema;

    const [, key] = activity.type.split(',');
    return getActivityValue(activityForm, key);
  }

  async uploadDocuments () {
    const { data: { id } } = this.props;
    /**
     * Generate base64 picture for each map
     * ['a', 'b'] => [{ key: 'activity-0', document: 'a' }, { key: 'activity-1', document: 'b' }]
     */
    const picturesArray = getCanvasPictures('.vanilla-map canvas').map((base64, index) => ({
      key: `activity-${index}`,
      document: base64,
    }));

    /**
     * We do no really need pictures in state. We juste have to know that backend has them
     * ['a', 'b'] => [{ key: 'activity-0' }, { key: 'activity-1' }]
     */
    const dummyPicturesArray = picturesArray.map(({ key }) => ({ key }));

    // Upload picture
    await uploadDocuments(id, picturesArray);


    // Optimistic update redux state that pictures are available for backend
    this.props.updateDocuments(dummyPicturesArray);
  }

  render () {
    const {
      data,
      data: { downloadables, id, properties: { parkings, activityTypes } },
      draftStatus,
      geojsonConflicts,
      activitySchema,
    } = this.props;

    const userrequestActivityTypes = (activityTypes && activityTypes.length)
      ? activityTypes
      : activitySchema;

    const activities = getActivitiesInfo(
      data.properties.activities,
      userrequestActivityTypes,
    );
    const hasParkings = data.properties.parkings && !!data.properties.parkings.length;

    return (
      <div>
        {data.id && data.state !== draftStatus && (
          <React.Fragment>
            <HeaderForm>
              {Object.keys(downloadables).length > 0 && (
                <Dropdown
                  overlay={(
                    <Menu onClick={this.handleDownloadableClick}>
                      {Object.keys(downloadables).map(key =>
                        <Menu.Item key={key}>{downloadables[key].title}</Menu.Item>)}
                    </Menu>
                  )}
                >
                  <Button type="primary-dark" icon="download">
                    Télécharger les documents
                  </Button>
                </Dropdown>
              )}
            </HeaderForm>

            <h1>Récapitulatif de la déclaration N<sup>o</sup>&nbsp;{data.id}</h1>

            <UserrequestContact data={data} />
          </React.Fragment>
        )}
        <Card style={{ marginTop: 21 }} title="Description de la déclaration">
          <p><strong>Nom</strong></p>
          <p>{data.properties.title}</p>

          <p><strong>Description</strong></p>
          <p>{data.properties.description}</p>
        </Card>

        <Card
          style={{ marginTop: 16 }}
          title={(
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <div>Description des activités</div>
              <Permissions groups={['staff']}>
                <Button
                  className="no-print"
                  type="primary"
                  icon="export"
                  onClick={this.handleExportClick}
                >
                  Exporter SIG
                </Button>
              </Permissions>
            </div>
          )}
        >
          <Collapse
            bordered={false}
            defaultActiveKey={
              [...Array.from({ length: activities.length }, (_, i) => i.toString())]
            }
            style={{ marginBottom: 24 }}
          >
            {activities.map((activity, i) => activity && (
              <Panel
                className={styles.customPanel}
                forceRender
                header={<strong>{`Activité ${i + 1} : ${this.getActivityProps(activity).label}`}</strong>}
                key={activity.uid || i}
              >
                <Activity
                  activity={activity}
                  geojsonConflicts={geojsonConflicts}
                  activityTypes={userrequestActivityTypes}
                  activityProps={this.getActivityProps(activity)}
                  data={data}
                />
              </Panel>
            ))}
          </Collapse>
        </Card>

        <Card style={{ marginTop: 16 }} title="Parkings et Véhicules">
          {hasParkings
            ? (
              <React.Fragment>
                <p><strong>Utilisation des parkings</strong></p>
                <ParkingsList
                  userrequestId={id}
                  parkings={parkings}
                  activities={activities}
                />
              </React.Fragment>
            )
            : <p><strong>Pas de parking réservé</strong></p>}

          <div style={{ marginTop: 16 }}>
            {data.properties.vehicles && data.properties.vehicles.licenses[0].name !== '' ? (
              <React.Fragment>
                <p>
                  <strong>
                    Déclaration impliquant la circulation d'un ou de plusieurs véhicules en forêt.
                  </strong>
                </p>
                <List
                  dataSource={data.properties.vehicles.licenses}
                  locale="Aucune déclaration."
                  bordered
                  renderItem={item => (
                    <List.Item
                      key={item.uid}
                    >
                      <List.Item.Meta
                        title={(
                          <React.Fragment>
                            <div>Propriétaire du véhicule :  <strong>{item.owner}</strong></div>
                            <div>Plaque d&apos;immatriculation : <strong> {item.name}</strong></div>
                          </React.Fragment>
                        )}
                        description={item.comment ? <div>Raison : {item.comment}</div> : null}
                      />
                    </List.Item>
                  )}
                />
              </React.Fragment>
            ) : (
              <React.Fragment>
                <p>
                  <strong>
                    Pas de véhicule devant circuler en forêt.
                  </strong>
                </p>
              </React.Fragment>
            )}
          </div>
        </Card>

        <Card className={styles.clarify} title="Informations additionnelles">
          <p><strong>Commentaire</strong></p>
          <p>{data.properties.comment ? data.properties.comment : 'Aucun commentaire de la part du demandeur.'}</p>
        </Card>
      </div>
    );
  }
}


const mapStateToProps = state => ({
  draftStatus: state.appConfig.states.DRAFT,
  geojsonConflicts: state.userrequest.geojsonConflicts,
  documents: state.userrequest.documents,
  activitySchema: state.admin.activities.list,
  layer: state.userrequest.layer,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({
    updateDocuments,
  }, dispatch);

Summary.propTypes = {
  draftStatus: PropTypes.number,
  data: PropTypes.shape({
    geojson: PropTypes.object,
    organisation: PropTypes.arrayOf(PropTypes.number),
    state: PropTypes.number,
    properties: PropTypes.object,
  }).isRequired,
};

Summary.defaultProps = {
  draftStatus: 100,
};

export default connect(mapStateToProps, mapDispatchToProps)(withAuthentication(Summary));



// WEBPACK FOOTER //
// ./src/components/Summary/Summary.js